import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, RefreshCw,
  Truck, Calendar, DollarSign, TrendingDown, AlertCircle
} from 'lucide-react';
import SupplierInvoiceForm from './SupplierInvoiceForm';
import apiClient from '../services/api';
import '../styles/SupplierInvoice.css';

const SupplierInvoice = () => {
  const [invoices, setInvoices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [showForm, setShowForm]         = useState(false);
  const [editInvoice, setEditInvoice]   = useState(null);   // null = new, obj = edit
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  /* ── session ── */
  const getSession = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session = getSession();

  useEffect(() => { loadInvoices(); }, []);

  /* ── load list ── */
  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/SupplierInvoices.php');
      if (res.data.success) {
        setInvoices(res.data.data || []);
      } else {
        throw new Error(res.data.message || 'Failed to load invoices');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── open / close form ── */
  const handleNew   = ()      => { setEditInvoice(null); setShowForm(true); };
  const handleEdit  = (inv)   => { setEditInvoice(inv);  setShowForm(true); };
  const handleClose = ()      => { setShowForm(false); setEditInvoice(null); };

  /* ── delete ── */
  const handleDelete = async (id, refNo) => {
    if (!window.confirm(`Delete invoice ${refNo}? This cannot be undone.`)) return;
    try {
      const res = await apiClient.delete('/SupplierInvoices.php', {
        params: { id }
      });
      if (res.data.success) {
        loadInvoices();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      alert('Error deleting: ' + (err.response?.data?.message || err.message));
    }
  };

  /* ── after form save ── */
  const handleSaved = () => {
    setShowForm(false);
    setEditInvoice(null);
    loadInvoices();
  };

  /* ── filter ── */
  const statuses = ['All', 'Pending', 'Approved', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'];

  const filtered = invoices.filter(inv => {
    const matchSearch =
      (inv.InvoiceNo      || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.OurReferenceNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.suppliername   || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'All' || inv.Status === statusFilter;
    return matchSearch && matchStatus;
  });

  /* ── status badge colour ── */
  const statusClass = (s) => {
    switch ((s || '').toLowerCase()) {
      case 'paid':           return 'si-badge-paid';
      case 'approved':       return 'si-badge-approved';
      case 'overdue':        return 'si-badge-overdue';
      case 'cancelled':      return 'si-badge-cancelled';
      case 'partially paid': return 'si-badge-partial';
      default:               return 'si-badge-pending';
    }
  };

  /* ── summary totals ── */
  const totalAmount  = filtered.reduce((s, i) => s + parseFloat(i.TotalAmount   || 0), 0);
  const totalBalance = filtered.reduce((s, i) => s + parseFloat(i.BalanceAmount || 0), 0);

  if (!session?.companyid && !session?.CompanyID) {
    return (
      <div className="si-page">
        <div className="si-no-session">
          <Truck size={40} />
          <h3>No Session</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="si-page">

      {/* ════════ PAGE HEADER ════════ */}
      <div className="si-page-header">
        <div className="si-page-header-left">
          <div className="si-page-icon">
            <Truck size={26} color="#fff" />
          </div>
          <div>
            <h1 className="si-page-title">Supplier Invoices</h1>
            <p className="si-page-subtitle">Cost invoices &amp; payables management</p>
          </div>
        </div>
        <div className="si-page-header-actions">
          <button className="si-btn-refresh" onClick={loadInvoices} title="Refresh" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'si-spin' : ''} />
          </button>
          <button className="si-btn-new" onClick={handleNew}>
            <Plus size={18} />
            New Invoice
          </button>
        </div>
      </div>

      {/* ════════ SUMMARY CARDS ════════ */}
      <div className="si-summary-cards">
        <div className="si-card">
          <div className="si-card-icon si-card-icon-orange"><Truck size={20} /></div>
          <div>
            <div className="si-card-value">{filtered.length}</div>
            <div className="si-card-label">Total Invoices</div>
          </div>
        </div>
        <div className="si-card">
          <div className="si-card-icon si-card-icon-red"><TrendingDown size={20} /></div>
          <div>
            <div className="si-card-value">
              {totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="si-card-label">Total Cost</div>
          </div>
        </div>
        <div className="si-card">
          <div className="si-card-icon si-card-icon-amber"><DollarSign size={20} /></div>
          <div>
            <div className="si-card-value">
              {totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="si-card-label">Outstanding Payable</div>
          </div>
        </div>
        <div className="si-card">
          <div className="si-card-icon si-card-icon-darkred"><AlertCircle size={20} /></div>
          <div>
            <div className="si-card-value">{invoices.filter(i => i.Status === 'Overdue').length}</div>
            <div className="si-card-label">Overdue</div>
          </div>
        </div>
      </div>

      {/* ════════ TOOLBAR ════════ */}
      <div className="si-toolbar">
        <div className="si-search-wrap">
          <Search size={16} className="si-search-icon" />
          <input
            className="si-search-input"
            type="text"
            placeholder="Search our ref, supplier invoice no, supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="si-search-clear" onClick={() => setSearchTerm('')}><X size={14} /></button>
          )}
        </div>
        <div className="si-status-tabs">
          {statuses.map(s => (
            <button
              key={s}
              className={`si-status-tab ${statusFilter === s ? 'si-status-tab-active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="si-result-count">
          {filtered.length} {filtered.length === 1 ? 'invoice' : 'invoices'}
        </div>
      </div>

      {/* ════════ CONTENT ════════ */}
      {loading ? (
        <div className="si-loading">
          <div className="si-spinner" />
          <span>Loading invoices...</span>
        </div>
      ) : error ? (
        <div className="si-error">
          <strong>Error:</strong> {error}
          <button className="si-btn-new" style={{ marginLeft: 16 }} onClick={loadInvoices}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="si-empty">
          <Truck size={52} style={{ opacity: 0.2, marginBottom: 12 }} />
          <h3>{searchTerm || statusFilter !== 'All' ? 'No matching invoices' : 'No supplier invoices yet'}</h3>
          <p>
            {searchTerm || statusFilter !== 'All'
              ? 'Try adjusting your search or filter'
              : 'Create your first supplier invoice to get started'}
          </p>
          {!searchTerm && statusFilter === 'All' && (
            <button className="si-btn-new" style={{ marginTop: 16 }} onClick={handleNew}>
              <Plus size={18} /> New Invoice
            </button>
          )}
        </div>
      ) : (
        <div className="si-table-wrap">
          <table className="si-table">
            <thead>
              <tr>
                <th>Our Ref No</th>
                <th>Supplier Inv No</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Supplier</th>
                <th>Currency</th>
                <th className="si-th-right">Sub Total</th>
                <th className="si-th-right">Tax</th>
                <th className="si-th-right">Total Amount</th>
                <th className="si-th-right">Balance</th>
                <th>Status</th>
                <th className="si-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.SupplierInvoiceID} className="si-tr">

                  <td>
                    <span
                      className="si-invoice-link"
                      onClick={() => handleEdit(inv)}
                      title="Click to edit"
                    >
                      {inv.OurReferenceNo}
                    </span>
                  </td>

                  <td className="si-mono">{inv.InvoiceNo || '—'}</td>

                  <td>
                    <div className="si-date-cell">
                      <Calendar size={12} />
                      {inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString() : '—'}
                    </div>
                  </td>

                  <td className={inv.Status !== 'Paid' && inv.DueDate && new Date(inv.DueDate) < new Date() ? 'si-overdue-date' : ''}>
                    {inv.DueDate ? new Date(inv.DueDate).toLocaleDateString() : '—'}
                  </td>

                  <td>
                    <div className="si-supplier-cell">
                      <Truck size={12} />
                      <div>
                        <div className="si-supplier-name">{inv.suppliername || '—'}</div>
                        {inv.suppliercode && <div className="si-supplier-code">{inv.suppliercode}</div>}
                      </div>
                    </div>
                  </td>

                  <td>{inv.currencycode || '—'}</td>

                  <td className="si-td-right si-mono">{parseFloat(inv.SubTotal      || 0).toFixed(2)}</td>
                  <td className="si-td-right si-mono">{parseFloat(inv.TaxAmount     || 0).toFixed(2)}</td>
                  <td className="si-td-right si-mono si-amount-bold">
                    <div className="si-amount-cell">
                      <DollarSign size={11} />
                      {parseFloat(inv.TotalAmount  || 0).toFixed(2)}
                    </div>
                  </td>
                  <td className="si-td-right si-mono">{parseFloat(inv.BalanceAmount || 0).toFixed(2)}</td>

                  <td>
                    <span className={`si-badge ${statusClass(inv.Status)}`}>
                      {inv.Status || 'Pending'}
                    </span>
                  </td>

                  <td className="si-td-center">
                    <div className="si-row-actions">
                      <button
                        className="si-action-btn si-action-edit"
                        onClick={() => handleEdit(inv)}
                        title="Edit Invoice"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="si-action-btn si-action-delete"
                        onClick={() => handleDelete(inv.SupplierInvoiceID, inv.OurReferenceNo)}
                        title="Delete Invoice"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ════════ FORM MODAL ════════ */}
      {showForm && (
        <SupplierInvoiceForm
          editData={editInvoice}
          onClose={handleClose}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default SupplierInvoice;